import { XIcon16Light, MinusCircleIcon16Light } from "@skbkontur/icons";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { Hint, ScrollContainer, Spinner } from "react-ui";
import { KonturColors } from "@skbkontur/colors";

import { getElementContentWidth, getElementWidthWithMargin } from "../../../../../Engine/utils";
import { UploadFileStatuses } from "../../../../../Engine/Core/ModalManager/Modals/Utils/Types";
import {
    AttachmentsItemInfo,
    AttachmentsItemLi,
    AttachmentsItemTitle,
    AttachmentsItemTitleText,
    AttachmentsListUl,
    DeleteBtnIcon,
    UnitSum,
} from "../FufWithAttachmentsUploadModal.styles";
import { UnitItem } from "../FufAndAttachmentsUploadModalTypes";
import { useFufAndAttachmentsUpload } from "../FufAndAttachmentsUploadContext";
import { removeItem } from "../FufAndAttachmentsReducer";
import { CurrencyHelper } from "../../../../Internals/ControlsReexport/CustomControls/CurrencyInput/CurrencyHelper";

const AttachmentsListItem = ({
    unitItem,
    scrollRef,
}: {
    unitItem: UnitItem;
    scrollRef: RefObject<ScrollContainer | null>;
}) => {
    const attachmentInfoRef = useRef<HTMLDivElement | null>(null);
    const liRef = useRef<HTMLLIElement | null>(null);
    const titleParagraphRef = useRef<HTMLParagraphElement | null>(null);
    const [title, setTitle] = useState<string>(unitItem.title);
    const truncatedTitleHalfLength = 20;

    useEffect(() => {
        const liEl = liRef.current;
        const attachmentInfoEl = attachmentInfoRef.current;
        const titleParagraphEl = titleParagraphRef.current;

        if (!liEl || !titleParagraphEl || !attachmentInfoEl) {
            return;
        }

        const maxContentWidth = getElementContentWidth(liEl) - getElementWidthWithMargin(attachmentInfoEl);

        if (titleParagraphEl.offsetWidth > maxContentWidth) {
            const itemTitle = unitItem.title;
            const truncatedText = `${itemTitle.slice(0, truncatedTitleHalfLength)}…${itemTitle.slice(-truncatedTitleHalfLength)}`;
            setTitle(truncatedText);

            setTimeout(() => {
                scrollRef?.current?.forceUpdate();
            }, 0);
        }
    }, [liRef.current, attachmentInfoRef.current]);

    const { dispatch } = useFufAndAttachmentsUpload();

    const ndsTotalSum = unitItem.file.ndsTotalSum
        ? `${CurrencyHelper.format(unitItem.file.ndsTotalSum.toString(), {
              fractionDigits: 2,
          })}${"\u00A0"}₽`
        : null;

    return (
        <AttachmentsItemLi ref={liRef}>
            <AttachmentsItemTitle>
                <AttachmentsItemTitleText
                    ref={titleParagraphRef}
                    hasError={unitItem.status === UploadFileStatuses.error}>
                    {title !== unitItem.title ? <Hint text={unitItem.title}>{title}</Hint> : title}
                </AttachmentsItemTitleText>
            </AttachmentsItemTitle>
            <AttachmentsItemInfo ref={attachmentInfoRef}>
                {ndsTotalSum && <UnitSum>{ndsTotalSum}</UnitSum>}
                {unitItem.status === UploadFileStatuses.error && (
                    <Hint text={unitItem.errorMessage}>
                        <DeleteBtnIcon onClick={() => dispatch(removeItem({ id: unitItem.id }))} title="Удалить">
                            <MinusCircleIcon16Light color={KonturColors.mainErrorTextRed90} />
                        </DeleteBtnIcon>
                    </Hint>
                )}
                {unitItem.status === UploadFileStatuses.success && (
                    <Hint text="Удалить">
                        <DeleteBtnIcon onClick={() => dispatch(removeItem({ id: unitItem.id }))}>
                            <XIcon16Light />
                        </DeleteBtnIcon>
                    </Hint>
                )}
                {unitItem.status === UploadFileStatuses.progress && (
                    <Hint text={unitItem.progressText}>
                        <Spinner type="mini" caption={null} />
                    </Hint>
                )}
            </AttachmentsItemInfo>
        </AttachmentsItemLi>
    );
};

export const AttachmentsList = ({
    items,
    scrollRef,
}: {
    items: UnitItem[];
    scrollRef: RefObject<ScrollContainer | null>;
}) => {
    const attachments = items.map(item => <AttachmentsListItem unitItem={item} key={item.id} scrollRef={scrollRef} />);

    return <AttachmentsListUl>{attachments}</AttachmentsListUl>;
};
